<template>
  <SfModalPortal
    modal-id="reports/create-forecast-modal"
    size="xs"
    @opened="openModal"
    @close="closeModal"
  >
    <template #title>{{ copy.title }}</template>

    <template #body>
      <div class="tw-flex tw-flex-col tw-gap-3">
        <!-- New forecast option -->
        <label
          class="
            tw-cursor-pointer
            tw-rounded-lg
            tw-border-2
            tw-border-solid
            tw-p-4
            tw-flex
            tw-items-start
            tw-gap-3
          "
          :class="
            selectedOption === 'new'
              ? 'tw-border-sf-primary tw-bg-cyan-50/50'
              : 'tw-border-sf-neutral-lighter-2 tw-bg-white'
          "
          data-testid="new-forecast-option"
        >
          <input
            v-model="selectedOption"
            type="radio"
            name="forecast-option"
            value="new"
            class="tw-mt-0.5"
          />
          <div class="tw-flex-1">
            <div class="tw-text-base tw-font-medium">
              {{ copy.newForecast.title }}
            </div>
            <div
              class="tw-text-sm"
              :class="
                selectedOption === 'new'
                  ? 'tw-text-sf-primary-dark'
                  : 'tw-text-sf-neutral-light-2'
              "
            >
              {{ copy.newForecast.subtitle }}
            </div>

            <div v-if="selectedOption === 'new'" class="tw-mt-3">
              <label class="tw-mb-1 tw-block tw-text-sm tw-font-normal">
                {{ copy.targetDropdownLabel }}
              </label>
              <select
                v-model="selectedTargetYear"
                class="tw-w-full tw-mb-4"
                :disabled="!targetYears.length"
              >
                <option v-if="!targetYears.length" :value="null">
                  {{ copy.loading }}
                </option>
                <option
                  v-for="targetYear in targetYears"
                  :key="'target-year-' + targetYear.toString()"
                  :value="targetYear"
                >
                  {{ targetYear }}
                </option>
              </select>

              <label class="tw-mb-1 tw-block tw-text-sm tw-font-normal">
                {{ copy.baseDropdown.label }}
              </label>
              <select
                v-model="selectedBase"
                class="tw-w-full"
                :disabled="!baseLedgers.length"
                data-testid="base-dropdown"
              >
                <option
                  v-if="!baseLedgers.length"
                  :value="{ baseLedger: null, baseBudget: null }"
                >
                  {{ copy.loading }}
                </option>

                <optgroup
                  v-if="baseLedgers.length"
                  :label="copy.baseDropdown.actuals"
                >
                  <option
                    v-for="baseLedger in baseLedgers"
                    :key="'base-ledger-' + baseLedger.id"
                    :value="{ baseLedger: baseLedger, baseBudget: null }"
                  >
                    {{ baseLedger.year }}
                  </option>
                </optgroup>
                <optgroup
                  v-if="baseBudgets.length"
                  :label="copy.baseDropdown.budget"
                >
                  <option
                    v-for="baseBudget in baseBudgets"
                    :key="
                      'base-budget-' + baseBudget.id + '-' + baseBudget.year
                    "
                    :value="{ baseLedger: null, baseBudget: baseBudget }"
                  >
                    {{ baseBudget.name }} ({{ baseBudget.year }})
                  </option>
                </optgroup>
              </select>
            </div>
          </div>
        </label>

        <!-- Load existing budget option -->
        <label
          class="
            tw-cursor-pointer
            tw-rounded-lg
            tw-border-2
            tw-border-solid
            tw-p-4
            tw-flex
            tw-items-start
            tw-gap-3
          "
          :class="
            selectedOption === 'existing'
              ? 'tw-border-sf-primary tw-bg-cyan-50/50'
              : 'tw-border-sf-neutral-lighter-2 tw-bg-white'
          "
          data-testid="load-existing-budget-option"
        >
          <input
            v-model="selectedOption"
            type="radio"
            name="forecast-option"
            value="existing"
            class="tw-mt-0.5"
          />
          <div class="tw-flex-1">
            <div class="tw-text-base tw-font-medium">
              {{ copy.loadExistingBudget.title }}
            </div>
            <div
              class="tw-text-sm"
              :class="
                selectedOption === 'existing'
                  ? 'tw-text-sf-primary-dark'
                  : 'tw-text-sf-neutral-light-2'
              "
            >
              {{ copy.loadExistingBudget.subtitle }}
            </div>

            <div v-if="selectedOption === 'existing'" class="tw-mt-3">
              <label class="tw-mb-1 tw-block tw-text-sm tw-font-normal">
                {{ copy.loadExistingBudget.dropdownLabel }}
              </label>
              <select
                v-model="selectedBudget"
                class="tw-w-full"
                :disabled="!baseBudgets.length"
                data-testid="load-budget-dropdown"
              >
                <option v-if="!baseBudgets.length" :value="null">
                  {{ copy.loadExistingBudget.noBudgets }}
                </option>
                <option
                  v-for="baseBudget in baseBudgets"
                  :key="'load-budget-' + baseBudget.id + '-' + baseBudget.year"
                  :value="baseBudget"
                >
                  {{ baseBudget.name }} ({{ baseBudget.year }})
                </option>
              </select>
            </div>
          </div>
        </label>
      </div>
    </template>

    <template #footer>
      <SfButtonDs variant="secondary" @click="closeModal">
        {{ copy.cancelButton }}
      </SfButtonDs>
      <SfButtonDs
        variant="primary"
        :disabled="submitDisabled"
        data-testid="create-forecast-submit"
        @click="handleSubmit"
      >
        {{ submitLabel }}
      </SfButtonDs>
    </template>
  </SfModalPortal>
</template>

<script setup lang="ts">
import type { Payload } from '../types/create-forecast-modal'
import type * as CreateModalDataResponse from '../types/api/create-modal-data-response'
import type { BaseLedger, BaseBudget } from '../types'

import { ref, computed } from 'vue'

import useI18n from '@app/shared/vue/composables/use-I18n'
import SFetch from '@app/utils/sfetch'

import { SfModalPortal } from '@app/shared/vue/components/sf-modal'
import { SfButtonDs } from '@silverfin/ui-components'

const I18n = useI18n()
const copy = {
  title: I18n.t('reports.forecast.create_forecast_modal.title'),
  newForecast: {
    title: I18n.t('reports.forecast.create_forecast_modal.new_forecast.title'),
    subtitle: I18n.t(
      'reports.forecast.create_forecast_modal.new_forecast.subtitle'
    ),
  },
  loadExistingBudget: {
    title: I18n.t(
      'reports.forecast.create_forecast_modal.load_existing_budget.title'
    ),
    subtitle: I18n.t(
      'reports.forecast.create_forecast_modal.load_existing_budget.subtitle'
    ),
    dropdownLabel: I18n.t(
      'reports.forecast.create_forecast_modal.load_existing_budget.dropdown_label'
    ),
    noBudgets: I18n.t(
      'reports.forecast.create_forecast_modal.load_existing_budget.no_budgets'
    ),
  },
  targetDropdownLabel: I18n.t(
    'reports.forecast.create_forecast_modal.target_dropdown_year_label'
  ),
  loading: I18n.t('common.loading'),
  baseDropdown: {
    label: I18n.t('reports.forecast.create_forecast_modal.base_dropdown.label'),
    actuals: I18n.t(
      'reports.forecast.create_forecast_modal.base_dropdown.actuals'
    ),
    budget: I18n.t(
      'reports.forecast.create_forecast_modal.base_dropdown.budget'
    ),
  },
  cancelButton: I18n.t('common.cancel'),
  createButton: I18n.t('reports.forecast.create_forecast_modal.create_button'),
  openButton: I18n.t('reports.forecast.create_forecast_modal.open_button'),
}

const selectedOption = ref<'new' | 'existing'>('new')

const targetYears = ref<number[]>([])
const selectedTargetYear = ref<number | null>(null)

const baseLedgers = ref<BaseLedger[]>([])
const baseBudgets = ref<BaseBudget[]>([])

const selectedBase = ref<{
  baseLedger: BaseLedger | null
  baseBudget: BaseBudget | null
}>({
  baseLedger: null,
  baseBudget: null,
})

const selectedBudget = ref<BaseBudget | null>(null)

const submitLabel = computed(() =>
  selectedOption.value === 'new' ? copy.createButton : copy.openButton
)

// The new-forecast button stays enabled while base data loads (see
// createNewForecast); the existing-budget path has nothing to wait for, so
// disable it when there are no saved budgets to open.
const submitDisabled = computed(
  () => selectedOption.value === 'existing' && !baseBudgets.value.length
)

let modalPayload: Payload

async function openModal(localModalPayload: Payload) {
  modalPayload = localModalPayload
  selectedOption.value = 'new'

  setModalData()
}

async function setModalData() {
  // Clear the previous data so the dropdowns fall back to their "Loading..."
  // state while the data is re-fetched on every reopen. The selections must
  // be reset too, otherwise they keep matching the stale (now removed) options
  // and the dropdowns render empty instead of the loading placeholder.
  targetYears.value = []
  selectedTargetYear.value = null
  baseLedgers.value = []
  baseBudgets.value = []
  selectedBase.value = { baseLedger: null, baseBudget: null }
  selectedBudget.value = null

  const dataFromServer = await fetchModalData()

  targetYears.value = dataFromServer.targetYears
  selectedTargetYear.value = dataFromServer.targetYears[0] ?? null

  baseLedgers.value = dataFromServer.baseLedgers

  dataFromServer.budgets.forEach((budget) => {
    budget.yearsWithData.forEach((year) => {
      baseBudgets.value.push({
        id: budget.id,
        name: budget.name,
        year: year,
      })
    })
  })

  selectedBase.value = {
    baseLedger: baseLedgers.value[0],
    baseBudget: null,
  }
  selectedBudget.value = baseBudgets.value[0] ?? null
}

async function fetchModalData(): Promise<CreateModalDataResponse.Data> {
  const url = window.Silverfin.Routes.report_forecast_create_modal_data_path(
    window.Silverfin.currentFirmId,
    window.Silverfin.currentCompanyId,
    modalPayload.reportId,
    { ledger_id: window.Silverfin.currentLedgerId() }
  )

  try {
    return (await SFetch.json(url)) as CreateModalDataResponse.Data
  } catch (error) {
    alert(I18n.t('common.error.try_again'))
    throw error
  }
}

function closeModal() {
  window.modalCitizen.closeModalPortal()
}

function handleSubmit() {
  if (selectedOption.value === 'new') {
    createNewForecast()
  } else {
    loadExistingBudget()
  }
}

function createNewForecast() {
  // Avoid sending incomplete requests to the BE before the data gets loaded.
  // The button is not disabled on purpose to avoid a flashing effect when it
  // gets enabled.
  if (!baseLedgers.value.length || !selectedTargetYear.value) {
    return
  }

  closeModal()

  modalPayload.onCreate({
    targetYear: selectedTargetYear.value,
    baseLedger: selectedBase.value.baseLedger,
    baseBudget: selectedBase.value.baseBudget,
    loadedBudgetId: null,
  })
}

function loadExistingBudget() {
  // The target year comes from the saved budget itself: loading an existing
  // budget continues the forecast for the year that budget holds data for.
  if (!selectedBudget.value) {
    return
  }

  closeModal()

  modalPayload.onCreate({
    targetYear: selectedBudget.value.year,
    baseLedger: null,
    baseBudget: selectedBudget.value,
    loadedBudgetId: selectedBudget.value.id,
  })
}
</script>
